import { Injectable, inject } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { Salesperson, SalespersonService } from '@vendasta/sales';
import { combineLatest, Observable, of } from 'rxjs';
import {
  catchError,
  delay,
  map,
  mergeMap,
  publishReplay,
  refCount,
  retryWhen,
  shareReplay,
  switchMap,
} from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { FeatureFlagService } from '@galaxy/partner';
import { Feature } from 'marketplace-ui';
import { StubAccessService } from '../core/access';
import { FeatureFlags } from '../core/features';

const DEFAULT_IMAGE_URL =
  'https://lh3.googleusercontent.com/70DGcv8fyAzy1n_cWh8GyVbsCIWzPGZMdBFiwJa8Ot-Ss5Rf_oZY8hKOG2wAttY4_iuU5TlO9NLlDrBGuEJBj6DHwRnGd2CJ32J2Im3c1g';

@Injectable()
export class AssignedSalespersonService {
  defaultImageUrl = DEFAULT_IMAGE_URL;
  delayTimes = [5, 5, 10, 10, 30, 30, 30, 60, 60, 60, 80];

  private readonly salespersonService = inject(SalespersonService);
  private readonly configService = inject(AppConfigService);
  private readonly accessService = inject(StubAccessService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly appConfigService = inject(AppConfigService);

  readonly partnerId$ = this.appConfigService.config$.pipe(map((config) => config.partnerId));
  readonly marketId$ = this.appConfigService.config$.pipe(map((config) => config.userMarket));

  salesperson$: Observable<Salesperson>;
  dialogConfig$: Observable<MatDialogConfig>;
  bookMeetingUrl$: Observable<string>;

  private readonly featureFlags$ = this.partnerId$.pipe(
    switchMap((partnerId) =>
      this.featureFlagService.batchGetStatus(partnerId, '', [
        FeatureFlags.INBOX_IN_PCC,
        FeatureFlags.INBOX_AI_ATLAS_LINK,
        FeatureFlags.INBOX_AI_SUPPORT_FEATURE_ID,
      ]),
    ),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly canAccessInbox$ = combineLatest([
    this.featureFlags$,
    this.accessService.hasAccessToFeature(Feature.inboxSalesContactUs),
  ]).pipe(
    map(([featureFlagStatus, hasAccess]) => {
      return featureFlagStatus[FeatureFlags.INBOX_IN_PCC] && hasAccess;
    }),
    shareReplay(1),
  );

  readonly canAccessInboxSalesContactUs$ = this.accessService.hasAccessToFeature(
    Feature.inboxSalesContactUs as Feature,
  );

  readonly canAccessInboxAiSupport$ = this.featureFlags$.pipe(
    map((featureFlagStatus) => featureFlagStatus[FeatureFlags.INBOX_AI_SUPPORT_FEATURE_ID]),
    shareReplay(1),
  );

  readonly canAccessInboxAiTab$ = this.featureFlags$.pipe(
    map((featureFlagStatus) => featureFlagStatus[FeatureFlags.INBOX_AI_ATLAS_LINK]),
    shareReplay(1),
  );

  constructor() {
    let newPartner = false;
    const assignedSalesPerson$: Observable<{ accountGroupId: string; salesperson: Salesperson }> = combineLatest([
      this.partnerId$,
      this.marketId$,
      this.configService.config$,
    ]).pipe(
      switchMap(([partnerId, marketId, partnerConfig]) => {
        if (partnerConfig.partnerCreated) {
          // check if partner was created within 1 day.
          const today = new Date();
          if (today.setDate(today.getDate() - 1) < partnerConfig.partnerCreated * 1000) {
            newPartner = true;
          }
        }
        return this.salespersonService.getAssignedSalespersonForPartner(partnerId, marketId || null);
      }),
      retryWhen((attempts) => {
        return attempts.pipe(
          mergeMap((error, i) => {
            if (!newPartner || i >= this.delayTimes.length || error.status >= 500) {
              throw new Error('Could not retrieve salesperson.');
            } else {
              return of(error).pipe(delay(this.delayTimes[i] * 1000));
            }
          }),
        );
      }),
      publishReplay(1),
      refCount(),
    );

    this.salesperson$ = assignedSalesPerson$.pipe(
      map((a) => a?.salesperson),
      catchError(() => of(null)),
      publishReplay(1),
      refCount(),
    );

    const businessId$ = assignedSalesPerson$.pipe(
      map((as) => as.accountGroupId),
      catchError(() => of(null)),
      publishReplay(1),
      refCount(),
    );

    this.dialogConfig$ = combineLatest([businessId$, this.salesperson$, this.canAccessInboxAiSupport$]).pipe(
      map(([businessId, salesperson, canAccessInboxAiSupport]) => {
        if (salesperson) {
          const dialogConfig = new MatDialogConfig();
          dialogConfig.disableClose = false;
          dialogConfig.autoFocus = false;
          dialogConfig.width = '550px';
          dialogConfig.maxHeight = '400px';
          dialogConfig.data = {
            imgUrl: salesperson?.photoUrlHttps,
            name: `${salesperson?.firstName || ''} ${salesperson?.lastName || ''}`.trim(),
            title: salesperson?.jobTitle,
            email: salesperson?.email,
            telephone: salesperson?.phoneNumber?.length > 0 ? salesperson.phoneNumber[0] : '',
            country: salesperson?.countryCode,
            salespersonId: salesperson?.salespersonId,
            businessId: businessId,
          };

          if (canAccessInboxAiSupport) {
            dialogConfig.maxHeight = '85vh';
            dialogConfig.maxWidth = '460px';
          }

          return dialogConfig;
        }
      }),
      publishReplay(1),
      refCount(),
    );

    this.bookMeetingUrl$ = this.salesperson$.pipe(map((sp) => sp?.meetingBookingUrl || ''));
  }
}
